// "Copy with sources": the answer with an `[n]` marker where each citation chip sits, plus a
// numbered source list. Markers are placed with the same snap rules as the chips
// (computeInsertPos), so a marker lands where the reader saw the chip: after the sentence,
// inside a table row's last cell, never inside code. Sources are numbered per
// (document, location) in reading order, so two chunks of the same page share one number.

import { citationLocation } from "@/lib/citationLocation";
import { codePointToUtf16 } from "@/runtime/attributionOffsets";
import { computeInsertPos, detectCodeRegions } from "@/runtime/attributionSentinels";
import { isAttributionCitation } from "@/runtime/citationDiscriminator";
import type { PersistedCitationRef } from "@/types";

export interface CopySource {
  number: number;
  title: string;
  location: string | null;
}

export interface CopyWithSources {
  text: string;
  // The answer alone with escaped markers (`\[n\]`), to render to HTML through markdown:
  // an unescaped `[1][2]` would parse as a reference link.
  markdown: string;
  sources: CopySource[];
}

function locationText(cite: PersistedCitationRef): string | null {
  const label = citationLocation(cite)?.label ?? null;
  return label?.startsWith("p.") ? `p. ${label.slice(2)}` : label;
}

export function sourceLine(source: CopySource): string {
  return `[${source.number}] ${source.title}${source.location ? `, ${source.location}` : ""}`;
}

// Only the attribution cohort carries answer offsets; older answers have nothing to place.
export function hasCopyableSources(citations: readonly PersistedCitationRef[]): boolean {
  return citations.some(isAttributionCitation);
}

export function buildCopyWithSources(
  content: string,
  citations: readonly PersistedCitationRef[],
): CopyWithSources | null {
  const regions = detectCodeRegions(content);
  const placed: Array<{ pos: number; ord: number; cite: PersistedCitationRef }> = [];
  citations.forEach((cite, ord) => {
    if (!isAttributionCitation(cite) || typeof cite.answer_span_end !== "number") return;
    const utf16End = codePointToUtf16(content, cite.answer_span_end);
    if (utf16End === null) return;
    const { pos, drop } = computeInsertPos(content, utf16End, regions);
    if (!drop) placed.push({ pos, ord, cite });
  });
  if (placed.length === 0) return null;
  placed.sort((a, b) => a.pos - b.pos || a.ord - b.ord);

  const numberByKey = new Map<string, number>();
  const sources: CopySource[] = [];
  const numbersAt = new Map<number, number[]>();
  for (const { pos, cite } of placed) {
    const location = locationText(cite);
    const key = `${cite.doc_id}\u0000${location ?? ""}`;
    let number = numberByKey.get(key);
    if (number === undefined) {
      number = sources.length + 1;
      numberByKey.set(key, number);
      sources.push({ number, title: cite.doc_title || cite.doc_id, location });
    }
    const atPos = numbersAt.get(pos) ?? [];
    if (!atPos.includes(number)) atPos.push(number);
    numbersAt.set(pos, atPos);
  }

  // `numbersAt` was filled in ascending position order, so one forward pass inserts every marker.
  const withMarkers = (marker: (n: number) => string): string => {
    let out = "";
    let last = 0;
    for (const [pos, numbers] of numbersAt) {
      out += content.slice(last, pos) + numbers.map(marker).join("");
      last = pos;
    }
    return out + content.slice(last);
  };

  return {
    text: `${withMarkers((n) => `[${n}]`)}\n\nSources\n${sources.map(sourceLine).join("\n")}`,
    markdown: withMarkers((n) => `\\[${n}\\]`),
    sources,
  };
}
